import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Select, Table } from "antd";
import IntlMessages from "@util/IntlMessages";
import "./UsersConfig.less";
import {
   getBoard,
   removeBoardMember,
   shareBoard,
   updateBoardMemberRole,
   getUsers
} from "../../../../../../../services/restAPI/RestApi";
import { useIntl } from "react-intl";
import { Message, Button, Popover, Switch, Popconfirm } from "../../../../../../BP2Components";
import { Icon_Delete, Icon_Save } from "../../../../../../BP2Components/Icons/icons";

const { Option } = Select;

const UsersConfig = ({ boardInfo, setShareOption }) => {
   const intl = useIntl();
   const [users, setUsers] = useState([]);
   const [dataSource, setDataSource] = useState();
   const [permissionSettings, setPermissionSettings] = useState({
      membersIds: [],
      showInMainNav: false,
      role: ""
   });

   const handleDelete = (userId, boardId) => () => {
      Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
      removeBoardMember(userId, boardId)
         .then(() => {
            setDataSource((prevDataSource) =>
               prevDataSource.filter((singleData) => singleData?.id !== userId)
            );
            Message.destroy();
            Message.success(intl.formatMessage({ id: "global.done" }));
         })
         .catch(() => {
            Message.destroy();
            Message.error(intl.formatMessage({ id: "global.error" }));
         });
   };

   const saveBoardMemberRole = (userInfo, boardId) => () => {
      const { id: memberId, permission, showInMainNav } = userInfo;
      Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
      updateBoardMemberRole(memberId, boardId, permission, showInMainNav ?? false)
         .then(() => {
            Message.destroy();
            Message.success(intl.formatMessage({ id: "global.done" }));
         })
         .catch(() => {
            Message.destroy();
            Message.error(intl.formatMessage({ id: "global.error" }));
         });
   };

   const handleChangeUserShareSettings = (userId, settingName) => (value) => {
      try {
         setDataSource((prevDataSource) => {
            const newDataSource = prevDataSource;
            const indexOfData = newDataSource.findIndex((singleData) => singleData.id === userId);
            newDataSource[indexOfData][settingName] = value;
            return newDataSource;
         });
      } catch (err) {
         Message.error(intl.formatMessage({ id: "global.error" }));
      }
   };

   const handleSavePermissions = (settings, boardId) => () => {
      const { membersIds, role, showInMainNav } = settings;
      Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
      shareBoard(boardId, membersIds, role, showInMainNav ?? false)
         .then((res) => {
            const membersInfo = res.data.members?.map((member) => ({
               id: member.id,
               name: member.member.firstName + " " + member.member.lastName,
               showInMainNav: member.showInMainNav,
               permission: member.memberType
            }));
            setDataSource(membersInfo);
            Message.destroy();
            Message.success(intl.formatMessage({ id: "global.done" }));
         })
         .catch(() => {
            Message.destroy();
            Message.error(intl.formatMessage({ id: "global.error" }));
         });
   };

   const columns = [
      {
         title: <IntlMessages id="boardsManager.name" />,
         dataIndex: "name",
         key: "name"
      },
      {
         title: <IntlMessages id="boardsManager.permission" />,
         dataIndex: "permission",
         key: "permission",
         render: (_, record) => (
            <Select
               defaultValue={record.permission}
               style={{ width: 150 }}
               onSelect={handleChangeUserShareSettings(record.id, "permission")}
            >
               <Option value="OWNER">
                  <IntlMessages id="boardsManager.owner" />
               </Option>
               <Option value="MODERATOR">
                  <IntlMessages id="boardsManager.moderator" />
               </Option>
               <Option value="COLLABOLATOR">
                  <IntlMessages id="boardsManager.collabolator" />
               </Option>
            </Select>
         )
      },
      {
         title: <IntlMessages id="boardsManager.showInMainNav" />,
         dataIndex: "showInMainNav",
         key: "showInMainNav",
         render: (_, record) => (
            <Switch
               defaultChecked={record.showInMainNav}
               onChange={handleChangeUserShareSettings(record.id, "showInMainNav")}
            />
         )
      },
      {
         title: <IntlMessages id="global.actions" />,
         dataIndex: "actions",
         key: "actinos",
         render: (_, record) => (
            <div style={{ display: "grid", gap: "0.5rem" }}>
               <Popover
                  title={intl.formatMessage({ id: "boardsManager.savePermissions" })}
                  placement="left"
                  trigger="hover"
                  dark={true}
               >
                  <Button
                     buttonType="primary"
                     onClick={saveBoardMemberRole(record, boardInfo.id)}
                     className="button"
                  >
                     <Icon_Save />
                  </Button>
               </Popover>
               <Popover
                  title={intl.formatMessage({ id: "boardsManager.deletePermissions" })}
                  placement="left"
                  trigger="hover"
                  dark={true}
               >
                  <Popconfirm
                     title={<IntlMessages id="settings.sureToDelete" />}
                     onConfirm={handleDelete(record.id, boardInfo.id)}
                  >
                     <Button buttonType="danger" ghost className="button">
                        <Icon_Delete />
                     </Button>
                  </Popconfirm>
               </Popover>
            </div>
         )
      }
   ];

   useEffect(() => {
      getUsers().then((res) => {
         setUsers(
            res.data.filter(
               ({ id, firstName, lastName }) => id != null && (!!firstName || !!lastName)
            )
         );

         getBoard(boardInfo.id).then((response) => {
            const membersInfo = response.data.members?.map((member) => ({
               id: member.id,
               name: member.member.firstName + " " + member.member.lastName,
               showInMainNav: member.showInMainNav,
               permission: member.memberType
            }));
            setDataSource(membersInfo);
         });
      });
   }, []);

   return (
      <div className="users-config">
         <div className="users-config__selection">
            <IntlMessages id="boardsManager.selectUsers" />
            <Select
               loading={!users.length}
               mode="multiple"
               onChange={(membersIds) =>
                  setPermissionSettings((prevSettings) => ({ ...prevSettings, membersIds }))
               }
               showSearch
               filterOption={(input, option) =>
                  option?.props?.children?.toLowerCase().includes(input.toLowerCase())
               }
            >
               {users?.map((user) => (
                  <Option key={user.oldId} value={user.id}>
                     {user.firstName + " " + user.lastName}
                  </Option>
               ))}
            </Select>
         </div>
         <div className="users-config__permission">
            <div className="users-config__permission__select">
               <IntlMessages id="boardsManager.selectPermission" />
               <Select
                  onChange={(role) =>
                     setPermissionSettings((prevSettings) => ({ ...prevSettings, role }))
                  }
               >
                  <Option value="OWNER" id="owner-option">
                     <IntlMessages id="boardsManager.owner" />
                  </Option>
                  <Option value="MODERATOR" id="moderator-option">
                     <IntlMessages id="boardsManager.moderator" />
                  </Option>
                  <Option value="COLLABOLATOR" id="collabolator-option">
                     <IntlMessages id="boardsManager.collabolator" />
                  </Option>
               </Select>
            </div>
            <div className="users-config__permission__switch">
               <IntlMessages id="boardsManager.showInMainNav" />
               <Switch
                  style={{ display: "block", margin: "auto" }}
                  onChange={(showInMainNav) =>
                     setPermissionSettings((prevSettings) => ({ ...prevSettings, showInMainNav }))
                  }
               />
            </div>
            <Button
               buttonType="primary"
               className="users-config__permission__save"
               onClick={handleSavePermissions(permissionSettings, boardInfo.id)}
            >
               <IntlMessages id="boardsManager.savePermissions" />
            </Button>
            {(permissionSettings.role === "OWNER" || permissionSettings.role === "MODERATOR") && (
               <div className="users-config__permission__describe">
                  <IntlMessages id="boardsManager.ownerAndModeratorPermission" />
               </div>
            )}
            {permissionSettings.role === "COLLABOLATOR" && (
               <div className="users-config__permission__describe">
                  <IntlMessages id="boardsManager.collaboratorPermission" />
               </div>
            )}
         </div>
         <Table
            size="small"
            dataSource={dataSource}
            loading={!dataSource}
            columns={columns}
            pagination={false}
         />

         <Button
            htmlType="button"
            ghost
            buttonType="primary"
            onClick={() => setShareOption()}
            className="button"
         >
            <IntlMessages id="global.goBack" />
         </Button>
      </div>
   );
};

UsersConfig.propTypes = {
   boardInfo: PropTypes.object.isRequired,
   setShareOption: PropTypes.func.isRequired
};

export default UsersConfig;
